import { create } from 'zustand';
import type { UserType } from '@/types';
import type L from 'leaflet';

interface AppState {
  // User state
  userType: UserType | null;
  setUserType: (type: UserType) => void;

  // Map state
  mapView: L.Map | null;
  setMapView: (view: L.Map | null) => void;

  // AOI state
  aoiGeometry: any | null;
  setAOIGeometry: (geometry: any | null) => void;
  isDrawingAOI: boolean;
  setIsDrawingAOI: (isDrawing: boolean) => void;

  // Zoom levels
  minZoom: number;
  maxZoom: number;
  setMinZoom: (zoom: number) => void;
  setMaxZoom: (zoom: number) => void;

  // Nautical overlay option
  includeNauticalOverlay: boolean;
  setIncludeNauticalOverlay: (include: boolean) => void;

  // Tile calculation
  tileCount: number;
  setTileCount: (count: number) => void;
  estimatedFileSize: number;
  setEstimatedFileSize: (size: number) => void;
  estimatedTime: number;
  setEstimatedTime: (time: number) => void;

  // Cursor position
  cursorLat: number;
  cursorLon: number;
  currentZoom: number;
  setCursorPosition: (lat: number, lon: number, zoom: number) => void;

  // Payment state
  isPaymentModalOpen: boolean;
  setIsPaymentModalOpen: (isOpen: boolean) => void;

  // Reset functions
  clearAOI: () => void;
  reset: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  // User state
  userType: null,
  setUserType: (type) => {
    set({ userType: type });
    localStorage.setItem('vortex_user_type', type);
  },

  // Map state
  mapView: null,
  setMapView: (view) => set({ mapView: view }),

  // AOI state
  aoiGeometry: null,
  setAOIGeometry: (geometry) => set({ aoiGeometry: geometry }),
  isDrawingAOI: false,
  setIsDrawingAOI: (isDrawing) => set({ isDrawingAOI: isDrawing }),

  // Zoom levels
  minZoom: 8,
  maxZoom: 14,
  setMinZoom: (zoom) => set({ minZoom: zoom }),
  setMaxZoom: (zoom) => set({ maxZoom: zoom }),

  // Nautical overlay option
  includeNauticalOverlay: false,
  setIncludeNauticalOverlay: (include) => set({ includeNauticalOverlay: include }),

  // Tile calculation
  tileCount: 0,
  setTileCount: (count) => set({ tileCount: count }),
  estimatedFileSize: 0,
  setEstimatedFileSize: (size) => set({ estimatedFileSize: size }),
  estimatedTime: 0,
  setEstimatedTime: (time) => set({ estimatedTime: time }),

  // Cursor position
  cursorLat: 0,
  cursorLon: 0,
  currentZoom: 8,
  setCursorPosition: (lat, lon, zoom) =>
    set({ cursorLat: lat, cursorLon: lon, currentZoom: zoom }),

  // Payment state
  isPaymentModalOpen: false,
  setIsPaymentModalOpen: (isOpen) => set({ isPaymentModalOpen: isOpen }),

  // Reset functions
  clearAOI: () =>
    set({
      aoiGeometry: null,
      tileCount: 0,
      estimatedFileSize: 0,
      estimatedTime: 0,
      isDrawingAOI: false
    }),

  reset: () =>
    set({
      userType: null,
      mapView: null,
      aoiGeometry: null,
      isDrawingAOI: false,
      minZoom: 8,
      maxZoom: 14,
      includeNauticalOverlay: false,
      tileCount: 0,
      estimatedFileSize: 0,
      estimatedTime: 0,
      cursorLat: 0,
      cursorLon: 0,
      currentZoom: 8,
      isPaymentModalOpen: false
    })
}));
